import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { Database, Download, Table2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useRunDataExtract } from '@/hooks/api/useReportsDepth';
import { downloadDataExtractCsv } from '@/services/api/reportsDepth.api';
import { DataExtractType } from '@pms/shared';
import type { DataExtractResult } from '@pms/shared';

const EXTRACT_LABELS: Record<DataExtractType, string> = {
  [DataExtractType.GUEST_INFO]: 'Guest Information',
  [DataExtractType.AVAILABILITY_BY_ROOM_TYPE]: 'Availability by Room Type',
  [DataExtractType.PAYMENT_ACTIVITY]: 'Payment Activity',
  [DataExtractType.CHECKOUT_REVIEW]: 'Checkout Review',
  [DataExtractType.REVENUE_SEGMENT]: 'Revenue by Segment',
  [DataExtractType.BOOKED_RESERVATIONS]: 'Booked Reservations',
};

export function DataExtractsPage() {
  const propertyId = useSelectedPropertyId();
  const [extractType, setExtractType] = useState<DataExtractType>(DataExtractType.GUEST_INFO);
  const [from, setFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [result, setResult] = useState<DataExtractResult | null>(null);
  const [exporting, setExporting] = useState(false);

  const runMutation = useRunDataExtract();

  const handleRun = (e: React.FormEvent) => {
    e.preventDefault();
    runMutation.mutate(
      { extractType, propertyId, from, to },
      { onSuccess: (data) => setResult(data) },
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadDataExtractCsv({ extractType, propertyId, from, to });
    } catch {
      // Download failed — loading state clears below
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Data Extracts"
        description="Build custom tabular exports across guests, availability, payments and revenue"
      />

      <form
        onSubmit={handleRun}
        className="bg-white rounded-xl border border-gray-200 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end"
      >
        <div className="lg:col-span-2">
          <label htmlFor="ex-type" className="block text-sm font-medium text-gray-700 mb-1">Extract type</label>
          <select
            id="ex-type"
            value={extractType}
            onChange={(e) => setExtractType(e.target.value as DataExtractType)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.values(DataExtractType).map((t) => (
              <option key={t} value={t}>{EXTRACT_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ex-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            id="ex-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="ex-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            id="ex-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2 sm:col-span-2 lg:col-span-4">
          <Button type="submit" isLoading={runMutation.isPending}>
            <Table2 size={16} aria-hidden="true" />
            Run extract
          </Button>
          <Button
            type="button"
            variant="secondary"
            isLoading={exporting}
            onClick={handleExport}
          >
            <Download size={16} aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </form>

      {!result ? (
        <EmptyState
          icon={Database}
          title="No extract yet"
          description="Choose an extract type and date range, then run it to preview the data."
        />
      ) : result.rows.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No rows for this range"
          description="Try widening the date range or a different extract type."
        />
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">
              {result.rows.length} row{result.rows.length === 1 ? '' : 's'} ·
              {' '}generated {new Date(result.generatedAt).toLocaleString()}
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {result.columns.map((col) => (
                    <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {col.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {result.rows.slice(0, 200).map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {result.columns.map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {row[col] === null || row[col] === undefined ? '--' : String(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.rows.length > 200 && (
            <p className="text-xs text-gray-400 mt-2">
              Showing first 200 rows. Export CSV for the full dataset.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
